import React, { useState } from 'react';
import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from 'reactstrap';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import EditIcon from '@material-ui/icons/Edit';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import BarChartIcon from '@material-ui/icons/BarChart';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import ArchiveIcon from '@material-ui/icons/Archive';
import DeleteIcon from '@material-ui/icons/Delete';
import styled from 'styled-components';
import { CampaignReduced, CampaignStatus } from '../../models';
import { useTranslations } from '../../store/translations/hooks';
import { TK } from '../../store/translations/translationKeys';
import AppButton from '../../components/AppButton';

const MenuItemContent = styled.span`
    display: flex;
    align-items: center;
    gap: var(--spacer-XS);

    svg {
        font-size: 1.1rem;
        opacity: 0.7;
    }
`;

const DangerItem = styled(DropdownItem)`
    && {
        color: var(--danger-color);
        &:hover { background-color: rgba(220, 53, 69, 0.08); }
    }
`;

export interface CampaignActionsMenuProps {
    campaign: CampaignReduced;
    onEdit: () => void;
    onAnalyze: () => void;
    onDuplicate: () => void;
    onArchiveOrDelete: () => void;
    onArchiveOrDeleteLabel: TK;
}

export const CampaignActionsMenu: React.FC<CampaignActionsMenuProps> = ({
    campaign,
    onEdit,
    onAnalyze,
    onDuplicate,
    onArchiveOrDelete,
    onArchiveOrDeleteLabel,
}) => {
    const t = useTranslations();
    const [open, setOpen] = useState(false);

    const canDelete = campaign.status === CampaignStatus.Draft || campaign.status === CampaignStatus.Ready;
    const canArchive = campaign.status !== CampaignStatus.Archived && !canDelete;
    const isDraft = campaign.status === CampaignStatus.Draft;

    return (
        <Dropdown isOpen={open} toggle={() => setOpen(current => !current)}>
            <DropdownToggle tag="span">
                <AppButton color="link" aria-label={t(TK.open)}>
                    <MoreVertIcon />
                </AppButton>
            </DropdownToggle>
            <DropdownMenu right positionFixed>
                <DropdownItem onClick={() => { setOpen(false); onEdit(); }}>
                    <MenuItemContent>
                        {isDraft ? <PlayArrowIcon /> : <EditIcon />}
                        {t(isDraft ? TK.continueDraft : TK.edit)}
                    </MenuItemContent>
                </DropdownItem>
                <DropdownItem onClick={() => { setOpen(false); onAnalyze(); }}>
                    <MenuItemContent>
                        <BarChartIcon />
                        {t(TK.analytics)}
                    </MenuItemContent>
                </DropdownItem>
                <DropdownItem onClick={() => { setOpen(false); onDuplicate(); }}>
                    <MenuItemContent>
                        <FileCopyIcon />
                        {t(TK.duplicateCampaign)}
                    </MenuItemContent>
                </DropdownItem>
                {(canArchive || canDelete) && (
                    <DangerItem onClick={() => { setOpen(false); onArchiveOrDelete(); }}>
                        <MenuItemContent>
                            {canDelete ? <DeleteIcon /> : <ArchiveIcon />}
                            {t(onArchiveOrDeleteLabel)}
                        </MenuItemContent>
                    </DangerItem>
                )}
            </DropdownMenu>
        </Dropdown>
    );
}
